import {useState} from 'react';
import {BottomSheet} from 'react-spring-bottom-sheet';
import {toast} from 'react-toastify';
import Button from '../../../../../components/button';
import InputField from '../../../../../components/simpleInput';
import SpinnerLoading from '../../../../../components/SpinnerLoading';
import {IBankAccountInfo} from '../../api/getBankAccountInfo/getBankAccountInfo';
import {IPostBankAccountInfoPayload} from '../../api/postBankAccountInfo/postBankAccountInfo';
import validateIban from '../../helpers/validateIban';

interface IBankAccountDataFillBottomSheetProps {
  open: boolean;
  onClose: () => void;
  onSubmit: ({
    bank_account_number,
    iban_number,
    bank_title,
  }: IPostBankAccountInfoPayload) => void;
  bankAccountData?: IBankAccountInfo;
  isLoading?: boolean;
}

const BANK_ACCOUNT_LENGTH = 16;
const IBAN_LENGTH = 24;

const BankAccountDataFillBottomSheet = ({
  open,
  onClose,
  onSubmit,
  bankAccountData,
  isLoading = false,
}: IBankAccountDataFillBottomSheetProps) => {
  const [accountNumber, setAccountNumber] = useState(
    bankAccountData?.bank_account_number ?? '',
  );

  const [ibanNumber, setIbanNumber] = useState(
    bankAccountData?.iban_number ?? '',
  );

  const normalizedAccountNumber = accountNumber.trim();
  const normalizedIban = ibanNumber.trim();

  const isAccountNumberValid = /^\d{16}$/.test(normalizedAccountNumber);

  const isIbanValid = validateIban(normalizedIban);

  const isSubmitDisabled = !isAccountNumberValid || !isIbanValid || isLoading;

  const handleSubmit = () => {
    if (
      accountNumber.trim() === bankAccountData?.bank_account_number?.trim() &&
      ibanNumber.trim() === bankAccountData?.iban_number?.trim()
    ) {
      toast.error(
        'شماره شبا و شماره حساب تغییری نکرده اند. برای اعمال تغییرات لطفا اطلاعات جدید را وارد کنید.',
      );
      return;
    } else
      onSubmit({
        bank_account_number: normalizedAccountNumber,
        iban_number: normalizedIban,
        bank_title: bankAccountData?.bank_title ?? 'اسم بانک',
      });
  };

  return (
    <BottomSheet
      open={open}
      onDismiss={() => !isLoading && onClose()}
      className="custom-bottom-sheet"
    >
      <div className="relative">
        {isLoading && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/60 backdrop-blur-sm">
            <SpinnerLoading size={36} />
          </div>
        )}

        <div
          className={`relative z-10 flex w-full flex-col gap-8 px-6 py-8 ${
            isLoading ? 'pointer-events-none select-none blur-sm' : ''
          }`}
        >
          <i
            className="absolute text-2xl text-gray-700 left-6 top-6 icon-nameClose-remove"
            onClick={() => onClose()}
          />

          <h2 className="mb-0 text-lg font-medium text-center text-gray-950">
            افزودن شماره حساب
          </h2>

          <div className="flex flex-col gap-6">
            <div>
              {/* Bank Account Number */}
              <InputField
                label="شماره حساب"
                labelClassName="pr-1 pt-0 pb-1 mb-0 text-sm"
                name="bankAccountNumber"
                inputMode="numeric"
                placeholder="شماره حساب را وارد کنید"
                required
                value={normalizedAccountNumber}
                onValueChange={value =>
                  setAccountNumber(
                    value.replace(/\D/g, '').slice(0, BANK_ACCOUNT_LENGTH),
                  )
                }
              />
              {normalizedAccountNumber.length > 0 && !isAccountNumberValid && (
                <span className="text-xs text-red-500">
                  شماره حساب باید دقیقاً ۱۶ رقم باشد.
                </span>
              )}
            </div>

            <div>
              <InputField
                label="شماره شبا"
                labelClassName="pr-1 pt-0 pb-1 mb-0 text-sm"
                name="ibanNumber"
                leftElement="IR"
                inputMode="numeric"
                placeholder="شماره شبا را وارد کنید"
                required
                value={normalizedIban}
                onValueChange={value =>
                  setIbanNumber(value.replace(/\D/g, '').slice(0, IBAN_LENGTH))
                }
              />

              {normalizedIban.length > 0 &&
                normalizedIban.length < IBAN_LENGTH && (
                  <span className="text-xs text-red-500">
                    شماره شبا باید شامل ۲۴ رقم باشد.
                  </span>
                )}

              {normalizedIban.length === IBAN_LENGTH && !isIbanValid && (
                <span className="text-xs text-red-500">
                  شماره شبا معتبر نیست.
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 mt-2">
            <Button
              variant="primary"
              isLoading={isLoading}
              disabled={isSubmitDisabled}
              onClick={handleSubmit}
            >
              تایید
            </Button>
          </div>
        </div>
      </div>
    </BottomSheet>
  );
};

export default BankAccountDataFillBottomSheet;
